import { useCallback, useEffect, useRef } from "react";

/**
 * A simple React hook for differentiating single and double clicks on the same component.
 * Returns a click handler function to be attached to the target element.
 *
 * @param {number} [latency=300] The amount of time (in milliseconds) to wait before differentiating a single from a double click
 * @param {function} onSingleClick A callback function for single click events
 * @param {function} onDoubleClick A callback function for double click events
 */

// Interface without ref
interface UseDoubleClickOptions {
  latency?: number;
  onSingleClick?: (event: React.MouseEvent) => void;
  onDoubleClick?: (event: React.MouseEvent) => void;
}

type useDoubleClickResult = (event: React.MouseEvent) => void;

export const useDoubleClick = (
  options: UseDoubleClickOptions,
): useDoubleClickResult => {
  const { latency = 200, onSingleClick, onDoubleClick } = options;

  const clickCount = useRef(0);
  const timer = useRef<number | null>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      clickCount.current += 1;

      if (timer.current) {
        window.clearTimeout(timer.current);
      }

      // If no onDoubleClick provided, trigger clicks immediately
      const finalLatency = onDoubleClick ? latency : 0;

      timer.current = window.setTimeout(() => {
        if (clickCount.current === 1 && onSingleClick) {
          onSingleClick(event);
        } else if (clickCount.current >= 2 && onDoubleClick) {
          onDoubleClick(event);
        }
        clickCount.current = 0;
      }, finalLatency);
    },
    [latency, onSingleClick, onDoubleClick],
  );

  useEffect(() => {
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
      }
    };
  }, []);

  return handleClick;
};
